import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import HomeLeftBar from '@common_components/layout/LeftBarManager';
import Footer from '../@common_components/global/footer.component';
import HomeTopBar from '../@common_components/global/homeTopBar.component';
import ChartLine from '../components/charts/line';
import Fraction from '../components/charts/Fraction';
import BackButton from '@common_components/buttons/BackButton.component';
import { WeeklyEngagementRate } from '../interfaces/typeCharts.interface';
import { eKpiLateDayRange, eKpiSpec } from 'enum/kpiSpec.enum';
import { KPIService } from 'services/kpi.service';
import { EventManagerService } from 'services/eventManagers.service';
import { useDispatch, useSelector } from 'react-redux';
import { Client, dayjs, RootState, UUID, CommonSiteService, CommonSectorService, usePersistantState } from '@umanforge/common';

import { EngagementDetail, EventCommitment } from 'interfaces/types.interface';
import { useTranslation } from 'react-i18next';
import { GenericTable } from 'components/table.component';
import EngagementModal from 'components/modals/engagements/engagementModal.component';
import { EventService } from 'services/events.service';
import { EngagementRateStats } from 'interfaces/kpi.interface';
import Tabs, { TabItem } from '@common_components/buttons/Tabs.component';
import TabsSlider from '@common_components/buttons/TabsSlider.component';
import { KEY } from 'constants/local_storage_key.constant';

function IndicatorsView() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer?.visualisationToken?.value; } );

  const chartText = [
    t( 'translation:late_engagements_not_completed_not_archived' ),
    t( 'translation:engagements_created_previous_week' ),
    t( 'translation:engagements_completed_or_archived_previous_week' ),
    t( 'translation:average_open_engagements_per_participant' ),
    t( 'translation:total_engagements_not_completed_not_archived' )
  ];

  const clients: Client = { id: 'Diary', name: 'Indicateurs' };

  const pages: TabItem[] = [
    { id: 'management', name: t( 'translation:management' ) },
    { id: 'late_engagements', name: t( 'translation:late_engagements' ) },
    { id: 'recurring_event', name: t( 'translation:recurring_event' ) }
  ];

  const [ indicatorValues, setIndicatorValues ] = useState<number[]>( [] );
  const [ engagementDetails, setEngagementDetails ] = useState<EngagementDetail[]>( [] );
  const [ isEngagementModalOpen, setIsEngagementModalOpen ] = useState<boolean>( false );
  const [ selectedEventCommitment, setSelectedEventCommitment ] = useState<EventCommitment | undefined>( undefined );

  const [ selectedPage, setSelectedPage ] = useState<number>( 0 );
  const [ selectedSpec, setActiveSector ] = useState<number>( 0 );

  const [ engagementRateStats, setEngagementRateStats ] = useState<EngagementRateStats | null>( null );

  const [ clientSelectedId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_CLIENT_ID, undefined );
  const [ siteSelectedId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SITE_ID, undefined );
  const [ sectorSelectedId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );
  const [ eventManagerId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_EVENT_MANAGER_ID, undefined );

  const [ eventManagerName, setEventManagerName ] = useState<string>( 'Event Manager' );
  const [ sectorName, setSectorName ] = useState<string>( 'Secteur' );
  const [ siteName, setSiteName ] = useState<string>( 'Site' );

  const [ sectors, setSectors ] = useState<{ id: string; name: string }[]>( [] );

  const [ datas, setDatas ] = useState<WeeklyEngagementRate>();
  const [ datas2, setDatas2 ] = useState<WeeklyEngagementRate>();
  const [ datas3, setDatas3 ] = useState<WeeklyEngagementRate>();
  const [ datas4, setDatas4 ] = useState<WeeklyEngagementRate>();
  const [ datas5, setDatas5 ] = useState<WeeklyEngagementRate>();
  const [ isFirstRender, setIsFirstRender ] = useState( true );

  useEffect( () => {
    const fetchElementNames = async () => {
      try {
        if ( siteSelectedId ) {
          const sites = await CommonSiteService.getAllowedSites( {}, token, dispatch );
          const foundSite = sites.find( site => { return site.id === siteSelectedId; } );
          if ( foundSite ) {
            setSiteName( foundSite.name );
          }
        }

        if ( sectorSelectedId && siteSelectedId ) {
          const sectorsData = await CommonSectorService.getAllowedSectorsBySitesId( [ siteSelectedId ], token, dispatch );
          const foundSector = sectorsData.find( sector => { return sector.id === sectorSelectedId; } );
          if ( foundSector ) {
            setSectorName( foundSector.name );
          }
        }


        if ( eventManagerId ) {
          try {
            const eventManager = await EventManagerService.getEventManagerById( eventManagerId, token, dispatch );
            if ( eventManager && eventManager.name ) {
              setEventManagerName( eventManager.name );
            }
          } catch ( error ) {
            console.error( 'Error fetching event manager:', error );
            setEventManagerName( `Event Manager ${eventManagerId.substring( 0, 5 )}` );
          }
        }
      } catch ( error ) {
        console.error( 'Error fetching element names:', error );
      }
    };

    fetchElementNames();
  }, [ siteSelectedId, sectorSelectedId, eventManagerId, token, dispatch, clients ] );

  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  useEffect( () => {
    const isSuperAdmin = me?.isSuperAdmin === true;
    const isClientAdmin = isSuperAdmin || (
      !!me?.clients?.find( c => { return c.id === clientSelectedId && c.isAdmin; } )
    );
    const isSiteAdmin = isSuperAdmin || (
      !!me?.clients?.find( c => { return c.sites?.find( s => { return s.id === siteSelectedId && s.isAdmin; } ); } )
    );
    interface Sector { id: string; isAdmin?: boolean; }
    interface Site { id: string; isAdmin?: boolean; sectors?: Sector[]; }
    interface ClientUser { id: string; isAdmin?: boolean; sites?: Site[]; }
    interface UserType { clients?: ClientUser[]; isSuperAdmin?: boolean; }

    function checkSectorAdmin( user: UserType | null, siteId: string | undefined, sectorId: string | undefined ): boolean {
      if ( !user?.clients ) { return false; }
      for ( const c of user.clients ) {
        if ( c.sites ) {
          for ( const s of c.sites ) {
            if ( s.id === siteId && s.sectors ) {
              if ( s.sectors.some( ( sec: Sector ) => { return sec.id === sectorId && sec.isAdmin; } ) ) {
                return true;
              }
            }
          }
        }
      }
      return false;
    }
    const isSectorAdmin = isSuperAdmin || checkSectorAdmin( me, siteSelectedId, sectorSelectedId );

    const options = [
      { id: '0', name: `${t( 'translation:this_table' )} (${eventManagerName})` },
    ];
    if ( isSectorAdmin ) {
      options.push( { id: '1', name: `${t( 'translation:this_sector' )} (${sectorName})` } );
      options.push( { id: '2', name: t( 'translation:all_sectors' ) } );
    }
    if ( isSiteAdmin ) {
      options.push( { id: '3', name: `${t( 'translation:this_site' )} (${siteName})` } );
    }
    if ( isClientAdmin ) {
      options.push( { id: '4', name: t( 'translation:all_sites' ) } );
    }
    setSectors( options );
  }, [ t, eventManagerName, sectorName, siteName, setSectors, me, clientSelectedId, siteSelectedId, sectorSelectedId ] );

  useEffect( () => {
    setIsFirstRender( false );
  }, [] );

  useEffect( () => {
    if ( !isFirstRender ) {
      if ( selectedPage === 0 ) {
        fetchEngagementRateStats();
        fetchWeeklyEngagements();
        fetchGlobalKpis();
      } else if ( selectedPage === 1 ) {
        fetchLateEngagement();
        fetchLateEngagementPerLateDays();
      } else if ( selectedPage === 2 ) {
        fetchRecurringEngagement();
        fetchRecurringEngagementForWeek();
      }
    }
  }, [ isFirstRender, clientSelectedId, siteSelectedId, sectorSelectedId, eventManagerId, selectedPage, selectedSpec ] );

  const handleSectorClick = ( sectorIndex: number ) => {
    setActiveSector( sectorIndex );
  };

  const handleTabSelect = ( index: number ) => {
    setSelectedPage( index );
    setEngagementDetails( [] );
  };

  const getSpecValue = (): { spec: number; id: string } => {
    let result = { spec: eKpiSpec.EVENT_MANAGER, id: eventManagerId ?? '' };
    if ( eventManagerId && clientSelectedId && siteSelectedId && sectorSelectedId ) {
      switch ( selectedSpec ) {
      case 0: result = { spec: eKpiSpec.EVENT_MANAGER, id: eventManagerId };
        break;
      case 1: result = { spec: eKpiSpec.SECTOR, id: sectorSelectedId };
        break;
      case 2: result = { spec: eKpiSpec.SECTORS, id: siteSelectedId };
        break;
      case 3: result = { spec: eKpiSpec.SITE, id: siteSelectedId };
        break;
      case 4: result = { spec: eKpiSpec.SITES, id: clientSelectedId };
        break;
      default: result = { spec: eKpiSpec.EVENT_MANAGER, id: eventManagerId };
        break;
      }
    }
    return result;
  };

  const fetchWeeklyEngagements = async () => {
    try {
      const { spec, id } = getSpecValue();
      const result = await KPIService.getWeeklyEngagementRate( spec, id, token, dispatch );
      result.ChartTitle = t( 'translation:weekly_engagement_rate_of_teams_sliding_4_weeks' );
      result.ChartNumber = 1;
      setDatas( result );
    } catch ( error ) {
      console.error( 'Error fetching weekly engagement:', error );
    }
  };

  const fetchGlobalKpis = async () => {
    try {
      const { spec, id } = getSpecValue();
      const result = await KPIService.getGlobalKPI( spec, id, token, dispatch );
      const values = [
        result?.lateEngagement ?? 0,
        result?.previousWeekCreatedEngagement ?? 0,
        result?.previousWeekEngagement ?? 0,
        result?.averageOpenEngagements ?? 0,
        result?.totalOpenEngagements ?? 0,
      ];
      setIndicatorValues( values );
    } catch ( error ) {
      console.error( 'Error fetching global KPIs:', error );
    }
  };

  const fetchLateEngagement = async () => {
    try {
      const { spec, id } = getSpecValue();
      const result = await KPIService.getLateEngagementPerTheme( spec, id, token, dispatch );
      result.ChartTitle = t( 'translation:late_engagements_pareto' );
      result.ChartNumber = 2;
      setDatas2( result );
    } catch ( error ) {
      console.error( 'Error fetching late engagement per theme:', error );
    }
  };

  const fetchLateEngagementPerLateDays = async () => {
    try {
      const { spec, id } = getSpecValue();
      const result = await KPIService.getLateEngagementPerRange( spec, id, token, dispatch );
      result.ChartTitle = t( 'translation:late_engagements_per_range_chart' );
      result.ChartNumber = 3;
      setDatas3( result );
    } catch ( error ) {
      console.error( 'Error fetching late engagement per range:', error );
    }
  };

  const fetchRecurringEngagement = async () => {
    try {
      const { spec, id } = getSpecValue();
      const result = await KPIService.getRecurringEngagementPerTheme( spec, id, token, dispatch );
      result.ChartTitle = t( 'translation:recurring_events_pareto' );
      result.ChartNumber = 4;
      setDatas4( result );
    } catch ( error ) {
      console.error( 'Error fetching recurring engagement per theme:', error );
    }
  };

  const fetchRecurringEngagementForWeek = async () => {
    try {
      const { spec, id } = getSpecValue();
      const result = await KPIService.getRecurringEngagementPerWeek( spec, id, token, dispatch );
      result.ChartTitle = t( 'translation:number_of_recurring_events_per_week' );
      result.ChartNumber = 5;
      setDatas5( result );
    } catch ( error ) {
      console.error( 'Error fetching recurring engagement per week:', error );
    }
  };

  const fetchEngagementRateStats = async () => {
    try {
      const { spec, id } = getSpecValue();
      const result = await KPIService.getEngagementRateStats( spec, id, token, dispatch );
      setEngagementRateStats( result );
    } catch ( error ) {
      console.error( 'Error fetching engagement rate stats:', error );
    }
  };

  const handleGetEngagementsByTheme = useCallback( async ( theme: string ) => {
    try {
      const { spec, id } = getSpecValue();
      const result = await KPIService.getLateEngagementDetailsByTheme( theme, spec, id, token, dispatch );
      if ( result && Array.isArray( result ) ) {
        setEngagementDetails( result );
      } else {
        console.warn( 'No engagement details found for the selected theme.' );
        setEngagementDetails( [] );
      }
    } catch ( error ) {
      console.error( 'Error fetching engagement details:', error );
      setEngagementDetails( [] );
    }
  }, [ selectedSpec, eventManagerId, sectorSelectedId, siteSelectedId, clientSelectedId, token, dispatch ] );

  const handleGetEngagementsByWeek = useCallback( async ( range: string ) => {
    try {
      const { spec, id } = getSpecValue();
      let convertedRange = range;
      switch ( range ) {
      case '1-6':
        convertedRange = eKpiLateDayRange.ONE_TO_SIX.toString();
        break;
      case '7-13':
        convertedRange = eKpiLateDayRange.SEVEN_TO_THIRTEEN.toString();
        break;
      case '14-20':
        convertedRange = eKpiLateDayRange.FOURTEEN_TO_TWENTY.toString();
        break;
      case '21-27':
        convertedRange = eKpiLateDayRange.TWENTY_ONE_TO_TWENTY_SEVEN.toString();
        break;
      case '28-34':
        convertedRange = eKpiLateDayRange.TWENTY_EIGHT_TO_THIRTY_FOUR.toString();
        break;
      case '35+':
        convertedRange = eKpiLateDayRange.THIRTY_FIVE_PLUS.toString();
        break;
      default:
        break;
      }
      const result = await KPIService.getLateEngagementPerRangeByRange( convertedRange, spec, id, token, dispatch );
      if ( result && Array.isArray( result ) ) {
        setEngagementDetails( result );
      } else {
        console.warn( 'No engagement details found for the selected week range.' );
        setEngagementDetails( [] );
      }
    } catch ( error ) {
      console.error( 'Error fetching engagement details by week range:', error );
      setEngagementDetails( [] );
    }
  }, [ selectedSpec, eventManagerId, sectorSelectedId, siteSelectedId, clientSelectedId, token, dispatch ] );

  const handleGetRecurringEngagementsByTheme = useCallback( async ( theme: string ) => {
    try {
      const { spec, id } = getSpecValue();
      const result = await KPIService.getRecurringEngagementPerThemeByTheme( theme, spec, id, token, dispatch );
      if ( result && Array.isArray( result ) ) {
        setEngagementDetails( result );
      } else {
        setEngagementDetails( [] );
      }
    } catch ( error ) {
      console.error( 'Error fetching recurring engagement details:', error );
      setEngagementDetails( [] );
    }
  }, [ selectedSpec, eventManagerId, sectorSelectedId, siteSelectedId, clientSelectedId, token, dispatch ] );

  const handleGetRecurringEngagementsByWeek = useCallback( async ( week: string ) => {
    try {
      const { spec, id } = getSpecValue();
      const result = await KPIService.getRecurringEngagementPerWeekByWeek( week, spec, id, token, dispatch );
      if ( result && Array.isArray( result ) ) {
        setEngagementDetails( result );
      } else {
        setEngagementDetails( [] );
      }
    } catch ( error ) {
      console.error( 'Error fetching recurring engagement details by week:', error );
      setEngagementDetails( [] );
    }
  }, [ selectedSpec, eventManagerId, sectorSelectedId, siteSelectedId, clientSelectedId, token, dispatch ] );

  const handleBackToEventManager = useCallback( () => {
    navigate( '/EventManager' );
  }, [ navigate ] );

  return (
    <div className='flex min-h-screen w-full bg-white-base text-black relative max-w-full'>
      <HomeLeftBar variant='basic' />
      <div className='w-full pr-6 pl-10 pb-5 flex flex-col min-w-0'>
        <HomeTopBar>
          <span className="font-comfortaa text-2xl">{clients.name}</span>
        </HomeTopBar>

        {/* Back button placed under the top bar */}
        <div className="mt-3">
          <BackButton onClick={handleBackToEventManager} title={t( 'translation:event_manager' )} label={t( 'translation:event_manager' )} />
        </div>

        <div className='w-full mt-0 h-full flex mb-12 flex-col rounded-md'>
          <div className="flex flex-col space-y-3 mb-3">
            <div className="flex justify-between items-center w-full">
              <div className="flex items-center mb-3" />

              <TabsSlider
                sectors={sectors}
                onSectorClick={handleSectorClick}
              />
            </div>
          </div>

          <Tabs items={pages} activeItem={selectedPage} onItemClick={handleTabSelect} />

          {/* Page 1: Management */}
          <div className={`py-4 h-full border px-4 flex flex-col rounded-b-2xl font-texte ${selectedPage === 0 ? 'flex' : 'hidden'}`}>
            <div className="flex gap-3 flex-wrap">
              {indicatorValues.map( ( value, index ) => {return (
                <div key={index} className="flex flex-col flex-shrink-0 w-full sm:w-auto sm:min-w-[160px] min-w-[120px] max-w-[260px] md:h-32 h-auto hover:bg-white-base shadow-md trans-fast mb-3 border rounded-2xl p-3 gap-2 bg-white">
                  <div className='w-full flex'>
                    <p
                      className="text-2xl px-3 py-1 text-left bg-orange-100 text-orange-600 rounded-xl font-semibold leading-tight"
                      style={{ textShadow: '0 1px 2px rgba(0,0,0,0.65)' }}
                      aria-label={String( value )}
                    >
                      {value}
                    </p>
                  </div>
                  <p className="text-left text-gray-500 text-sm whitespace-normal break-words">{chartText[ index % chartText.length ]}</p>
                </div>
              );} )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
              <div className="bg-white rounded-xl shadow-md border flex flex-col overflow-hidden p-3">
                <div className="flex flex-col items-start gap-2 py-2">
                  <span className="text-gray-500 text-sm">{t( 'translation:this_week' )}</span>
                  <span className="text-2xl font-semibold">
                    {engagementRateStats?.currentWeek != null
                      ? `${engagementRateStats.currentWeek}%`
                      : '--'}
                  </span>
                </div>
                <div className="flex flex-col items-start gap-2 py-2 bg-gray-50 p-2 rounded">
                  <span className="text-gray-500 text-sm">{t( 'translation:previous_week' )}</span>
                  <span className="text-2xl font-semibold">
                    {engagementRateStats?.previousWeek != null
                      ? `${engagementRateStats.previousWeek}%`
                      : '--'}
                  </span>
                </div>
                <div className={`flex flex-col items-start gap-2 py-2 p-2 mt-2 rounded
                  ${( engagementRateStats?.variation ?? 0 ) > 0 ? 'text-green-600 bg-green-50' : ( engagementRateStats?.variation ?? 0 ) < 0 ? 'text-red-600 bg-red-50' : 'text-gray-600 bg-gray-50'}`}>
                  <span className="text-gray-500 text-sm">{t( 'translation:variation' )}</span>
                  <span className='text-2xl font-semibold'>
                    {engagementRateStats?.variation != null
                      ? `${engagementRateStats.variation > 0 ? '+' : ''}${engagementRateStats.variation}%`
                      : '--'}
                  </span>
                </div>
                <div className="w-full border-t pt-3 mt-3">
                  <div className="flex flex-col items-start">
                    <span className="text-gray-700 text-sm font-semibold mb-1">
                      {t( 'translation:engagement_rate_4_weeks' )}
                    </span>
                    <div className="bg-gray-50 rounded-lg px-3 py-2 mt-2 flex flex-col items-start border">
                      <span className="text-xs text-gray-600 font-medium mb-1">{ t( 'translation:engagement_rate_calculation' ) }</span>
                      <div className="flex flex-col items-start">
                        <Fraction
                          numerator={ <span className="text-sm text-gray-800 font-semibold">{t( 'translation:nb_engagements_completed_on_time' )}</span> }
                          denominator={ <span className="text-xs text-gray-500 mt-1">{t( 'translation:nb_engagements_completed_or_abandoned' )}</span> }
                          className="w-full items-start"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white border shadow-md w-full h-full p-4 rounded-xl flex flex-col justify-start items-start col-span-2">
                {datas?.ChartTitle && <h3 className="text-base font-semibold text-gray-700 mb-3 mr-6 pr-2 truncate max-w-full">{datas.ChartTitle}</h3>}
                {datas?.series && (
                  <ChartLine
                    series={datas.series}
                    xAxis={datas.xAxis}
                    yAxis={datas.yAxis}
                    ChartNumber={datas.ChartNumber || 0}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Page 2: Engagements en retard */}
          <div className={`py-5 h-full border px-4 flex flex-col md:flex-row rounded-b-2xl font-texte gap-5 ${selectedPage === 1 ? 'flex' : 'hidden'}`}>
            <div className='bg-white shadow-md w-full p-4 rounded-xl'>
              {datas2?.ChartTitle && <h3 className="text-sm font-semibold text-gray-700 mb-3 mr-6 pr-2 truncate max-w-full">{datas2.ChartTitle}</h3>}
              {datas2?.series && (
                <ChartLine
                  series={datas2.series}
                  xAxis={datas2.xAxis}
                  yAxis={datas2.yAxis}
                  ChartNumber={datas2.ChartNumber || 0}
                  functionOnClick={handleGetEngagementsByTheme}
                />
              )}
            </div>
            <div className='bg-white shadow-md w-full p-4 rounded-xl'>
              {datas3?.ChartTitle && <h3 className="text-sm font-semibold text-gray-700 mb-3 mr-6 pr-2 truncate max-w-full">{datas3.ChartTitle}</h3>}
              {datas3?.series && (
                <ChartLine
                  series={datas3.series}
                  xAxis={datas3.xAxis}
                  yAxis={datas3.yAxis}
                  ChartNumber={datas3.ChartNumber || 0}
                  functionOnClick={handleGetEngagementsByWeek}
                />
              )}
            </div>
          </div>

          {/* Page 3: Évènements récurrents */}
          <div className={`py-5 h-full border px-4 flex flex-col md:flex-row rounded-b-2xl gap-5 font-texte ${selectedPage === 2 ? 'flex' : 'hidden'}`}>
            <div className='bg-white shadow-md w-full p-4 rounded-xl'>
              {datas4?.ChartTitle && <h3 className="text-sm font-semibold text-gray-700 mb-3 mr-6 pr-2 truncate max-w-full">{datas4.ChartTitle}</h3>}
              {datas4?.series && (
                <ChartLine
                  series={datas4.series}
                  xAxis={datas4.xAxis}
                  yAxis={datas4.yAxis}
                  ChartNumber={datas4.ChartNumber || 0}
                  functionOnClick={handleGetRecurringEngagementsByTheme}
                />
              )}
            </div>
            <div className='bg-white shadow-md w-full p-4 rounded-xl'>
              {datas5?.ChartTitle && <h3 className="text-sm font-semibold text-gray-700 mb-3 mr-6 pr-2 truncate max-w-full">{datas5.ChartTitle}</h3>}
              {datas5?.series && (
                <ChartLine
                  series={datas5.series}
                  xAxis={datas5.xAxis}
                  yAxis={datas5.yAxis}
                  ChartNumber={datas5.ChartNumber || 0}
                  functionOnClick={handleGetRecurringEngagementsByWeek}
                />
              )}
            </div>
          </div>

          {( selectedPage === 1 || selectedPage === 2 ) && (
            engagementDetails && engagementDetails.length > 0 ? (
              <div className=" mb-10 rounded-2xl border border-gray-200 shadow-lg bg-white mt-8">
                <div className="px-4 py-3 bg-gray-50 border-b">
                  <h3 className="font-semibold text-lg">{t( 'translation:engagement_details' )}</h3>
                </div>
                <GenericTable<EngagementDetail>
                  title=""
                  renderList={ engagementDetails.length }
                  getData={ ( page: number, step: number, { key, order }: { key: string, order: 'asc' | 'desc' } ) => {
                    const sortedData = [ ...engagementDetails ].sort( ( a, b ) => {
                      let valueA: unknown = a[ key as keyof EngagementDetail ];
                      let valueB: unknown = b[ key as keyof EngagementDetail ];

                      if ( key === 'deliveryDate' ) {
                        valueA = a.deliveryDate ? dayjs( a.deliveryDate ).valueOf() : 0;
                        valueB = b.deliveryDate ? dayjs( b.deliveryDate ).valueOf() : 0;
                      }

                      if ( key === 'participants' ) {
                        valueA = Array.isArray( a.participants ) ? a.participants.join( ', ' ) : '';
                        valueB = Array.isArray( b.participants ) ? b.participants.join( ', ' ) : '';
                      }

                      const strA = String( valueA || '' ).toLowerCase();
                      const strB = String( valueB || '' ).toLowerCase();

                      if ( strA < strB ) {
                        return order === 'asc' ? -1 : 1;
                      }
                      if ( strA > strB ) {
                        return order === 'asc' ? 1 : -1;
                      }
                      return 0;
                    } );

                    const startIndex = ( page - 1 ) * step;
                    const endIndex = startIndex + step;
                    const paginatedData = sortedData.slice( startIndex, endIndex );

                    return Promise.resolve( { total: engagementDetails.length, datas: paginatedData } );
                  } }
                  columns={[
                    {
                      key: 'title',
                      label: t( 'common:title' ),
                      type: 'string',
                      className: 'px-4 py-3 text-gray-900 font-semibold whitespace-nowrap',
                      getValue: detail => {return detail.title ;},
                    },
                    {
                      key: 'deliveryDate',
                      label: t( 'translation:engagement_date' ),
                      type: 'string',
                      className: 'px-4 py-3 text-gray-700 whitespace-nowrap',
                      getValue: detail => {return detail.deliveryDate ? dayjs( detail.deliveryDate ).format( 'L' ) : ' - ';},
                    },
                    {
                      key: 'theme',
                      label: t( 'common:theme' ),
                      type: 'string',
                      className: 'px-4 py-3 text-gray-700 whitespace-nowrap',
                      getValue: detail => {return detail.theme ;},
                    },
                    {
                      key: 'participants',
                      label: t( 'common:participants' ),
                      type: 'string',
                      className: 'px-4 py-3 text-gray-700 whitespace-nowrap',
                      getValue: detail => {return Array.isArray( detail.participants ) ? detail.participants.join( ', ' ) : ' - ';},
                    },
                  ]}
                  onRowClick={ async ( row: EngagementDetail ) => {
                    const eventId = row.eventId ?? row.id ?? null;
                    if ( !eventId ) { return; }
                    try {
                      const eventData = await EventService.getEventById( eventId, token, dispatch );

                      const firstEngagement = eventData.engagements && eventData.engagements.length > 0 ? eventData.engagements[ 0 ] : undefined;

                      const commitment: EventCommitment = {
                        participants: [],
                        eventId: eventData.event.id || eventId,
                        eventManagerIds: firstEngagement?.eventManagerIds || [],
                        deliveryDate: firstEngagement?.deliveryDate || '',
                        completed: Boolean( firstEngagement?.completed ),
                        completedDate: firstEngagement?.completedDate || '',
                        archived: Boolean( firstEngagement?.archived ),
                        eventTitle: eventData.event.title || '',
                        color: firstEngagement?.color || '',
                        isEventRecurring: ( eventData.event.recurring ? 1 : 0 ),
                        haveNote: Array.isArray( eventData.notes ) && eventData.notes.length > 0,
                        haveDocument: Array.isArray( eventData.documents ) && eventData.documents.length > 0,
                        numberOfLateDays: 0,
                        complatedPercentage: 0,
                        id: firstEngagement?.id,
                        authorFullName: eventData.event.authorFullname || '',
                        creationDate: eventData.event.createdDate || ''
                      };

                      setSelectedEventCommitment( commitment );
                      setIsEngagementModalOpen( true );
                    } catch ( error ) {
                      console.error( 'Error fetching event data for modal:', error );
                    }
                  } }
                />
              </div>
            ) : (
              <div className="p-5 bg-gray-50 rounded-xl shadow-md text-center mt-8">
                <h2 className="text-xl font-bold mb-2">{t( 'translation:no_engagement_details_available' )}</h2>
              </div>
            )
          )}
        </div>
      </div>
      {/* Engagement Modal */}
      { isEngagementModalOpen && selectedEventCommitment && (
        <EngagementModal
          onClose={() => {
            setIsEngagementModalOpen( false );
            setSelectedEventCommitment( undefined );
          }}
          engagementClicked={ selectedEventCommitment }
          isViewMode={ visualisationToken != null }
        />
      )}
      <Footer />
    </div>
  );
}

export default IndicatorsView;
